import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Plus, FolderOpen, Building2, Trash2, ChevronRight, ChevronLeft, ChevronDown,
  Upload, FileImage, Layers as LayersIcon, ArrowLeft, MoreVertical, Pencil, X, Scissors, Loader2, Eye, LogOut, User, Shield, Lock, Users
} from 'lucide-react';
import FloorElementList from './FloorElementList';
import Paywall from './Paywall';
import LVEditorModal from './LVEditorModal';
import { useModal } from './components/CustomModal';
import { useAuth } from './AuthContext';
import axios from 'axios';
import API from './apiConfig';
axios.defaults.withCredentials = true;

const FLOOR_LEVELS = {
  '-2': '2. Untergeschoss',
  '-1': 'Kellergeschoss',
  '0': 'Erdgeschoss',
  '1': '1. Obergeschoss',
  '2': '2. Obergeschoss',
  '3': '3. Obergeschoss',
  '4': 'Dachgeschoss',
  '5': '4. Obergeschoss',
  '6': '5. Obergeschoss',
};

const FLOOR_ABBR = {
  '-2': 'U2',
  '-1': 'KG',
  '0': 'EG',
  '1': 'OG1',
  '2': 'OG2',
  '3': 'OG3',
  '4': 'DG',
  '5': 'OG4',
  '6': 'OG5',
};

// Top to bottom = Dachgeschoss → 2. Untergeschoss (wie ein echtes Gebäude)
const FLOOR_ORDER = ['4', '6', '5', '3', '2', '1', '0', '-1', '-2'];

// Render-time coercion: GPT-5.2 sometimes returns rich `{value, confidence, ...}`
// objects where a scalar is expected. Return a renderable value (number/string)
// so React doesn't throw "Objects are not valid as a React child".
function S(v, fallback = '') {
  if (v == null) return fallback;
  if (typeof v === 'number' || typeof v === 'string' || typeof v === 'boolean') return v;
  if (Array.isArray(v)) return v.map(x => S(x, '')).filter(Boolean).join(', ');
  if (typeof v === 'object') {
    if (v.value != null) return S(v.value, fallback);
    // Find first scalar member (e.g. {ebene0_to_ebene1: 3.0, ...})
    for (const [k, val] of Object.entries(v)) {
      if (k === 'confidence' || k === 'unit' || k === 'notes' || k === 'note' ||
          k === 'source' || k === 'basis' || k === 'calculation' || k === 'method' ||
          k === 'context' || k === 'source_text' || k === 'source_text_tokens') continue;
      if (typeof val === 'number' || typeof val === 'string') return val;
    }
    return fallback;
  }
  return fallback;
}

// ─── PROJECT LIST VIEW ──────────────────────────────────────────

function ProjectCreateWizard({ onClose, onCreated }) {
  const [step, setStep] = useState('upload'); // 'upload' | 'analyzing' | 'confirm'
  const [file, setFile] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [fileB64, setFileB64] = useState(null);
  const [filename, setFilename] = useState('');
  const [name, setName] = useState('');
  const [location, setLocation] = useState('');
  const [client, setClient] = useState('');
  const [description, setDescription] = useState('');
  const [floor, setFloor] = useState('0');
  const [creating, setCreating] = useState(false);
  const fileRef = useRef(null);

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setStep('analyzing');

    const formData = new FormData();
    formData.append('file', f);
    try {
      const resp = await fetch(`${API}/api/projects/analyze-metadata`, { method: 'POST', body: formData });
      if (resp.ok) {
        const data = await resp.json();
        const s = data.suggestions || {};
        setSuggestions(s);
        setFileB64(data.file_b64);
        setFilename(data.filename || f.name);
        setName(s.name || f.name.replace(/\.[^.]+$/, ''));
        setLocation(s.location || '');
        setClient(s.client || '');
        setDescription(s.description || '');
        if (s.floor) {
          const fl = s.floor.toLowerCase();
          if (fl.includes('keller')) setFloor('-1');
          else if (fl.includes('1. og') || fl.includes('1.og') || fl.includes('obergeschoss')) setFloor('1');
          else if (fl.includes('dach') || fl.includes('dg')) setFloor('4');
          else setFloor('0');
        }
        setStep('confirm');
      } else {
        setName(f.name.replace(/\.[^.]+$/, ''));
        setStep('confirm');
      }
    } catch (err) {
      console.error(err);
      setName(f.name.replace(/\.[^.]+$/, ''));
      setStep('confirm');
    }
  };

  const handleCreate = async () => {
    if (!name.trim()) return;
    setCreating(true);
    try {
      // 1. Create project
      const resp = await fetch(`${API}/api/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          description: [location, client, description].filter(Boolean).join(' — '),
        }),
      });
      if (!resp.ok) return;
      const project = await resp.json();
      const projectId = project.project_id;

      // 2. Upload floor plan to the new project (triggers full pipeline)
      let analysisId = null;
      if (file) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('project_id', projectId);
        formData.append('floor_level', floor);
        const uploadResp = await fetch(`${API}/api/analyze`, { method: 'POST', body: formData, credentials: 'include', headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` } });
        if (uploadResp.ok) {
          const uploadData = await uploadResp.json();
          analysisId = uploadData.analysis_id || uploadData.id;
          // If async processing, wait for completion
          if (uploadData.status === 'processing' && analysisId) {
            for (let i = 0; i < 120; i++) {
              await new Promise(r => setTimeout(r, 5000));
              try {
                const pollResp = await fetch(`${API}/api/analyses/${analysisId}`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` } });
                if (pollResp.ok) {
                  const pollData = await pollResp.json();
                  if (pollData.status === 'completed' || pollData.status === 'failed') break;
                }
              } catch { /* retry */ }
            }
          }
        }
      }

      onCreated(projectId, analysisId);
    } catch (err) {
      console.error(err);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="mb-8 p-5 bg-zinc-900 border border-zinc-700 rounded-lg" data-testid="create-wizard">
      {/* Step indicator */}
      <div className="flex items-center gap-3 mb-4">
        {['upload', 'analyzing', 'confirm'].map((s, i) => (
          <div key={s} className={`flex items-center gap-1.5 font-mono text-[10px] ${step === s ? 'text-blue-400' : i < ['upload','analyzing','confirm'].indexOf(step) ? 'text-green-400' : 'text-zinc-600'}`}>
            <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold border ${step === s ? 'border-blue-500 bg-blue-500/20' : i < ['upload','analyzing','confirm'].indexOf(step) ? 'border-green-500 bg-green-500/20' : 'border-zinc-700'}`}>{i + 1}</div>
            <span>{s === 'upload' ? 'Hochladen' : s === 'analyzing' ? 'Analyse' : 'Bestätigen'}</span>
          </div>
        ))}
      </div>

      {/* Step 1: Upload */}
      {step === 'upload' && (
        <div className="space-y-3">
          <p className="font-mono text-sm text-zinc-400">Laden Sie einen Grundriss hoch. Die Projektinformationen werden automatisch erkannt.</p>
          <input ref={fileRef} type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg" onChange={handleUpload} />
          <div
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-blue-500', 'bg-blue-500/5'); }}
            onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove('border-blue-500', 'bg-blue-500/5'); }}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.classList.remove('border-blue-500', 'bg-blue-500/5');
              const f = e.dataTransfer.files[0];
              if (f) handleUpload({ target: { files: [f] } });
            }}
            className="w-full flex flex-col items-center justify-center gap-3 py-10 border-2 border-dashed border-zinc-700 hover:border-blue-500 rounded-lg text-zinc-500 hover:text-blue-400 transition-all cursor-pointer"
            data-testid="upload-dropzone"
          >
            <Upload className="w-8 h-8" />
            <span className="font-mono text-sm">Grundriss hierher ziehen oder klicken</span>
            <span className="font-mono text-[10px] text-zinc-600">PDF, PNG, JPG</span>
          </div>
          <button onClick={onClose} className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-md font-mono text-sm transition-colors">
            Abbrechen
          </button>
        </div>
      )}

      {/* Step 2: Analyzing */}
      {step === 'analyzing' && (
        <div className="flex flex-col items-center gap-3 py-8">
          <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
          <span className="font-mono text-sm text-zinc-400">Projektinformationen werden erkannt...</span>
          <span className="font-mono text-[10px] text-zinc-600">{file?.name}</span>
        </div>
      )}

      {/* Step 3: Confirm */}
      {step === 'confirm' && (
        <div className="space-y-3">
          {suggestions && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-500/5 border border-green-500/20 rounded text-[10px] font-mono text-green-400">
              <Eye className="w-3 h-3" />
              Projektdaten automatisch erkannt — bitte prüfen und ggf. anpassen
            </div>
          )}
          <div>
            <label className="font-mono text-[10px] text-zinc-500 block mb-1">Projektname *</label>
            <input data-testid="wizard-name" type="text" value={name} onChange={e => setName(e.target.value)}
              className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-md font-mono text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-mono text-[10px] text-zinc-500 block mb-1">Ort / Adresse</label>
              <input type="text" value={location} onChange={e => setLocation(e.target.value)} placeholder="z.B. 38154 Königslutter"
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-md font-mono text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
            </div>
            <div>
              <label className="font-mono text-[10px] text-zinc-500 block mb-1">Bauherr</label>
              <input type="text" value={client} onChange={e => setClient(e.target.value)} placeholder="z.B. Fam. Müller"
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-md font-mono text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-mono text-[10px] text-zinc-500 block mb-1">Geschoss</label>
              <select value={floor} onChange={e => setFloor(e.target.value)}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-md font-mono text-sm focus:outline-none">
                <option value="-2">2. Untergeschoss</option>
                <option value="-1">Kellergeschoss</option>
                <option value="0">Erdgeschoss</option>
                <option value="1">1. Obergeschoss</option>
                <option value="2">2. Obergeschoss</option>
                <option value="3">3. Obergeschoss</option>
                <option value="5">4. Obergeschoss</option>
                <option value="6">5. Obergeschoss</option>
                <option value="4">Dachgeschoss</option>
              </select>
            </div>
            <div>
              <label className="font-mono text-[10px] text-zinc-500 block mb-1">Beschreibung</label>
              <input type="text" value={description} onChange={e => setDescription(e.target.value)} placeholder="optional"
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-md font-mono text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
            </div>
          </div>
          <div className="flex items-center gap-2 mt-1 text-[10px] font-mono text-zinc-600">
            <FileImage className="w-3 h-3" />
            {filename || file?.name}
          </div>
          <div className="flex gap-3 pt-1">
            <button data-testid="wizard-create-btn" onClick={handleCreate} disabled={creating || !name.trim()}
              className="flex-1 px-5 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 rounded-md font-mono text-sm font-medium transition-colors">
              {creating ? 'Wird angelegt & analysiert...' : 'Projekt anlegen & Grundriss analysieren'}
            </button>
            <button onClick={onClose} className="px-4 py-2.5 bg-zinc-800 hover:bg-zinc-700 rounded-md font-mono text-sm transition-colors">
              Abbrechen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


function UserMenu({ onNavigate }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  if (!user) return null;
  const initials = `${(user.first_name || '')[0] || ''}${(user.last_name || '')[0] || ''}`.toUpperCase() || 'U';
  return (
    <div className="relative">
      <button data-testid="user-menu-btn" onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 px-2 py-1.5 hover:bg-zinc-800 rounded-md transition-colors">
        <div className="w-7 h-7 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-mono font-bold">{initials}</div>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-56 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl z-[200]">
          <div className="px-3 py-2 border-b border-zinc-800">
            <p className="font-mono text-xs text-white">{user.first_name} {user.last_name}</p>
            <p className="font-mono text-[10px] text-zinc-500">{user.email}</p>
            {user.role === 'admin' && <span className="inline-block mt-1 px-1.5 py-0.5 bg-amber-500/20 text-amber-400 text-[9px] font-mono rounded">Admin</span>}
          </div>
          {onNavigate && (
            <button data-testid="profile-btn" onClick={() => { setOpen(false); onNavigate('profile'); }}
              className="w-full px-3 py-2 flex items-center gap-2 text-zinc-300 hover:bg-zinc-800 text-xs font-mono transition-colors">
              <User className="w-3.5 h-3.5" /> Mein Profil
            </button>
          )}
          {onNavigate && (
            <button data-testid="team-btn" onClick={() => { setOpen(false); onNavigate('team'); }}
              className="w-full px-3 py-2 flex items-center gap-2 text-zinc-300 hover:bg-zinc-800 text-xs font-mono transition-colors">
              <Users className="w-3.5 h-3.5" /> Team verwalten
            </button>
          )}
          {user.role === 'admin' && onNavigate && (
            <button data-testid="admin-btn" onClick={() => { setOpen(false); onNavigate('admin'); }}
              className="w-full px-3 py-2 flex items-center gap-2 text-amber-400 hover:bg-zinc-800 text-xs font-mono transition-colors">
              <Shield className="w-3.5 h-3.5" /> Admin Dashboard
            </button>
          )}
          <button data-testid="logout-btn" onClick={logout}
            className="w-full px-3 py-2 flex items-center gap-2 text-red-400 hover:bg-zinc-800 text-xs font-mono transition-colors border-t border-zinc-800">
            <LogOut className="w-3.5 h-3.5" /> Abmelden
          </button>
        </div>
      )}
    </div>
  );
}


export function ProjectListView({ onSelectProject, onOpenAnalysis, onNavigate }) {
  const { user } = useAuth();
  const modal = useModal();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [creating, setCreating] = useState(false);
  const [unassigned, setUnassigned] = useState([]);
  const [viewMode, setViewMode] = useState(localStorage.getItem('projectViewMode') || 'grid');
  const [assigningId, setAssigningId] = useState(null); // analysis_id being assigned
  const [assignProject, setAssignProject] = useState('');
  const [assignFloor, setAssignFloor] = useState('0');

  const fetchProjects = useCallback(async () => {
    try {
      const resp = await fetch(`${API}/api/projects`);
      const data = await resp.json();
      setProjects(data.projects || []);
    } catch (e) {
      console.error('Failed to fetch projects', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const createProject = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const resp = await fetch(`${API}/api/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName.trim(), description: newDesc.trim() }),
      });
      if (resp.ok) {
        setNewName('');
        setNewDesc('');
        setShowCreate(false);
        fetchProjects();
      }
    } catch (e) {
      console.error('Failed to create project', e);
    } finally {
      setCreating(false);
    }
  };

  const deleteProject = async (e, projectId) => {
    e.stopPropagation();
    if (!await modal.confirm('Projekt und alle zugehörigen Grundrisse wirklich löschen?', { title: 'Projekt löschen', confirmText: 'Löschen', type: 'warning' })) return;
    try {
      await fetch(`${API}/api/projects/${projectId}`, { method: 'DELETE' });
      fetchProjects();
    } catch (e) {
      console.error('Failed to delete project', e);
    }
  };

  const fetchUnassigned = useCallback(async () => {
    try {
      const resp = await fetch(`${API}/api/analyses/unassigned`);
      const data = await resp.json();
      setUnassigned(data.analyses || []);
    } catch (e) {
      console.error('Failed to fetch unassigned', e);
    }
  }, []);

  useEffect(() => { fetchUnassigned(); }, [fetchUnassigned]);

  const assignAnalysis = async (analysisId) => {
    if (!assignProject) return;
    try {
      const resp = await fetch(`${API}/api/analyses/${analysisId}/assign`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: assignProject, floor_level: parseInt(assignFloor) }),
      });
      if (resp.ok) {
        setAssigningId(null);
        setAssignProject('');
        setAssignFloor('0');
        fetchUnassigned();
        fetchProjects();
      }
    } catch (e) {
      console.error('Failed to assign analysis', e);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      {showPaywall && (
        <div className="fixed inset-0 z-[100] bg-zinc-950/95 backdrop-blur-sm overflow-y-auto" data-testid="paywall-overlay">
          <Paywall
            title="Premium-Funktion"
            subtitle="Sie haben Ihre kostenlose Test-Analyse genutzt. Um weitere Projekte oder Pläne hinzuzufügen, schalten Sie LAIzyBlueprint frei."
            hideTrialOption
            onClose={() => setShowPaywall(false)}
          />
        </div>
      )}

      {/* Header */}
      <div className="border-b border-zinc-800 bg-zinc-900/80 backdrop-blur-sm sticky top-0 z-[100]">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => onNavigate && onNavigate('landing')} className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-testid="logo-home-btn">
            <img src="/logo.png" alt="LAIzyBlueprint" className="h-10 w-auto" />
            <span className="font-mono text-base font-medium tracking-tight">LAIzyBlueprint</span>
          </button>
          <div className="flex items-center gap-3">
            {(() => {
              // Lock the button if user is not privileged AND already has 1+ projects
              const isPrivileged = user?.role === 'admin' || user?.subscription_status === 'active';
              const used = (projects?.length || 0) >= 1;
              const locked = !isPrivileged && used;
              return (
                <button
                  data-testid="create-project-btn"
                  onClick={() => { if (locked) { setShowPaywall(true); } else { setShowCreate(true); window.scrollTo({ top: 0, behavior: 'smooth' }); } }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-mono font-medium transition-colors ${
                    locked
                      ? 'bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-300'
                      : 'bg-blue-600 hover:bg-blue-500 text-white'
                  }`}
                  title={locked ? 'Premium-Funktion — jetzt freischalten' : ''}
                >
                  {locked ? <Lock className="w-3.5 h-3.5 text-amber-400" /> : <Plus className="w-4 h-4" />}
                  Neues Projekt
                </button>
              );
            })()}
            <UserMenu onNavigate={onNavigate} />
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Create dialog — multi-step wizard */}
        {showCreate && (
          <ProjectCreateWizard
            onClose={() => setShowCreate(false)}
            onCreated={(projectId, analysisId) => {
              setShowCreate(false);
              fetchProjects();
              if (analysisId && onOpenAnalysis) {
                onOpenAnalysis(analysisId);
              } else if (projectId) {
                onSelectProject(projectId);
              }
            }}
          />
        )}

        {/* Layout toggle */}
        <div className="flex justify-end mb-4 gap-1" data-testid="view-toggle">
          <button onClick={() => { setViewMode('grid'); localStorage.setItem('projectViewMode', 'grid'); }}
            className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-zinc-700 text-white' : 'text-zinc-500 hover:text-zinc-300'}`} title="Kacheln">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 16 16"><rect x="1" y="1" width="6" height="6" rx="1"/><rect x="9" y="1" width="6" height="6" rx="1"/><rect x="1" y="9" width="6" height="6" rx="1"/><rect x="9" y="9" width="6" height="6" rx="1"/></svg>
          </button>
          <button onClick={() => { setViewMode('list'); localStorage.setItem('projectViewMode', 'list'); }}
            className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-zinc-700 text-white' : 'text-zinc-500 hover:text-zinc-300'}`} title="Liste">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 16 16"><rect x="1" y="2" width="14" height="2.5" rx="0.5"/><rect x="1" y="6.75" width="14" height="2.5" rx="0.5"/><rect x="1" y="11.5" width="14" height="2.5" rx="0.5"/></svg>
          </button>
        </div>

        {/* Project grid / list */}
        {loading ? (
          <div className="text-center text-zinc-500 py-20 font-mono text-sm">Projekte werden geladen...</div>
        ) : projects.length === 0 ? (
          <div className="text-center py-20">
            <FolderOpen className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500 font-mono text-sm">Noch keine Projekte vorhanden.</p>
            <p className="text-zinc-600 font-mono text-xs mt-1">Erstelle dein erstes Projekt, um Grundrisse hochzuladen.</p>
          </div>
        ) : viewMode === 'list' ? (
          <div className="space-y-2" data-testid="project-list">
            {projects.map((p) => (
              <div
                key={p.project_id}
                onClick={() => onSelectProject(p.project_id)}
                className="flex items-center gap-4 p-3 bg-zinc-900/60 border border-zinc-800 rounded-lg cursor-pointer hover:border-zinc-600 hover:bg-zinc-800/60 transition-all group"
                data-testid={`project-list-item-${p.project_id}`}
              >
                <FolderOpen className="w-5 h-5 text-zinc-600 group-hover:text-amber-400 transition-colors shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-sm text-zinc-200 truncate">{p.name}</div>
                  {p.description && <div className="font-mono text-xs text-zinc-500 truncate">{p.description}</div>}
                </div>
                {p.creator_name && (
                  <span className="text-[10px] font-mono bg-zinc-800 text-zinc-400 px-2 py-0.5 rounded-full shrink-0">{p.creator_name}</span>
                )}
                <span className="font-mono text-xs text-zinc-500 shrink-0">
                  {p.updated_at ? new Date(p.updated_at).toLocaleDateString('de-DE') : ''}
                </span>
                <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-zinc-400 shrink-0" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="project-grid">
            {projects.map((p) => (
              <div
                key={p.project_id}
                data-testid={`project-card-${p.project_id}`}
                onClick={() => onSelectProject(p.project_id)}
                className="group relative p-5 bg-zinc-900 border border-zinc-800 hover:border-zinc-600 rounded-lg cursor-pointer transition-all hover:bg-zinc-900/80"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-md bg-blue-500/10 flex items-center justify-center">
                      <Building2 className="w-4 h-4 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-mono text-sm font-medium">{p.name}</h3>
                      {p.description && (
                        <p className="font-mono text-xs text-zinc-500 mt-0.5 truncate max-w-[180px]">{p.description}</p>
                      )}
                    </div>
                  </div>
                  <button
                    data-testid={`delete-project-${p.project_id}`}
                    onClick={(e) => deleteProject(e, p.project_id)}
                    className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-red-500/20 rounded-md transition-all"
                  >
                    <Trash2 className="w-3.5 h-3.5 text-red-400" />
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-zinc-500">
                      {p.floor_count || 0} {(p.floor_count || 0) === 1 ? 'Grundriss' : 'Grundrisse'}
                    </span>
                    {p.creator_name && !p.is_own && (
                      <span className="text-[9px] font-mono bg-zinc-800 text-zinc-500 px-1.5 py-0.5 rounded-full">{p.creator_name}</span>
                    )}
                  </div>
                  <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-zinc-400 transition-colors" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ─── NICHT ZUGEORDNET (Unassigned analyses) — nur Admin ─── */}
        {user?.role === 'admin' && unassigned.length > 0 && (
          <div className="mt-10" data-testid="unassigned-container">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-md bg-amber-500/10 flex items-center justify-center">
                <FileImage className="w-3.5 h-3.5 text-amber-400" />
              </div>
              <h2 className="font-mono text-sm font-medium text-zinc-300">Nicht zugeordnet</h2>
              <span className="font-mono text-[10px] text-zinc-600 bg-zinc-800 px-2 py-0.5 rounded-full">{unassigned.length}</span>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden divide-y divide-zinc-800/50">
              {unassigned.map((a) => (
                <div key={a.analysis_id} className="group" data-testid={`unassigned-${a.analysis_id}`}>
                  <div className="flex items-center gap-3 px-4 py-3 hover:bg-zinc-800/50 transition-colors">
                    <button
                      data-testid={`open-unassigned-${a.analysis_id}`}
                      onClick={() => onOpenAnalysis && onOpenAnalysis(a.analysis_id)}
                      className="flex-1 flex items-center gap-3 text-left min-w-0">
                      <FileImage className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <span className="font-mono text-xs text-zinc-300 block truncate">{a.filename}</span>
                        <div className="flex items-center gap-3 mt-0.5">
                          <span className="font-mono text-[10px] text-zinc-600">
                            {new Date(a.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                          </span>
                          {a.processing_time && (
                            <span className="font-mono text-[10px] text-zinc-600">{a.processing_time}s</span>
                          )}
                          {a.svg_data?.metadata?.total_area_m2 && (
                            <span className="font-mono text-[10px] text-green-500/70">{a.svg_data.metadata.total_area_m2} m²</span>
                          )}
                        </div>
                      </div>
                    </button>
                    {assigningId === a.analysis_id ? (
                      <div className="flex items-center gap-2 flex-shrink-0" data-testid={`assign-form-${a.analysis_id}`}>
                        <select
                          data-testid={`assign-project-select-${a.analysis_id}`}
                          value={assignProject}
                          onChange={(e) => setAssignProject(e.target.value)}
                          className="px-2 py-1.5 bg-zinc-800 border border-zinc-600 rounded-md font-mono text-[11px] text-zinc-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="">Projekt...</option>
                          {projects.map((p) => (
                            <option key={p.project_id} value={p.project_id}>{p.name}</option>
                          ))}
                        </select>
                        <select
                          data-testid={`assign-floor-select-${a.analysis_id}`}
                          value={assignFloor}
                          onChange={(e) => setAssignFloor(e.target.value)}
                          className="px-2 py-1.5 bg-zinc-800 border border-zinc-600 rounded-md font-mono text-[11px] text-zinc-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          {FLOOR_ORDER.map((lvl) => (
                            <option key={lvl} value={lvl}>{FLOOR_LEVELS[lvl]}</option>
                          ))}
                        </select>
                        <button
                          data-testid={`confirm-assign-${a.analysis_id}`}
                          onClick={() => assignAnalysis(a.analysis_id)}
                          disabled={!assignProject}
                          className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 rounded-md font-mono text-[11px] transition-colors"
                        >
                          OK
                        </button>
                        <button
                          onClick={() => { setAssigningId(null); setAssignProject(''); setAssignFloor('0'); }}
                          className="p-1 hover:bg-zinc-700 rounded-md transition-colors"
                        >
                          <X className="w-3.5 h-3.5 text-zinc-500" />
                        </button>
                      </div>
                    ) : (
                      <button
                        data-testid={`assign-btn-${a.analysis_id}`}
                        onClick={() => { setAssigningId(a.analysis_id); setAssignProject(''); setAssignFloor('0'); }}
                        className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-md font-mono text-[11px] text-zinc-400 hover:text-white transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0"
                      >
                        Zuordnen
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ─── PROJECT DETAIL VIEW ────────────────────────────────────────

function RoofGeometryCard({ projectId, project, onUpdate }) {
  const [open, setOpen] = useState(false);
  const [eave, setEave] = useState('');
  const [ridge, setRidge] = useState('');
  const [angle, setAngle] = useState('');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const sa = project?.section_analysis;
    if (sa?.roof_angle_deg && !angle) setAngle(String(sa.roof_angle_deg));
  }, [project]);

  useEffect(() => {
    const rg = project?.roof_geometry;
    if (!rg) return;
    if (rg.eave_height_m) setEave(String(rg.eave_height_m));
    if (rg.ridge_height_m) setRidge(String(rg.ridge_height_m));
    if (rg.angle_deg) setAngle(String(rg.angle_deg));
    setResult(rg);
  }, [project?.roof_geometry]);

  const handleSave = async (e) => {
    e.preventDefault();
    const eaveV = parseFloat(eave.replace(',', '.'));
    const ridgeV = parseFloat(ridge.replace(',', '.'));
    const angleV = parseFloat(angle.replace(',', '.'));
    if (!eaveV || !ridgeV || !angleV) return;
    setSaving(true);
    try {
      const resp = await fetch(`${API}/api/projects/${projectId}/roof-geometry`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ eave_height_m: eaveV, ridge_height_m: ridgeV, angle_deg: angleV }),
      });
      if (resp.ok) { const d = await resp.json(); setResult(d.roof_geometry); if (onUpdate) onUpdate(); }
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  if (!open) {
    return (
      <button data-testid="open-roof-card-btn" onClick={() => setOpen(true)}
        className="w-full flex flex-col items-center justify-center gap-2 py-4 border border-dashed border-zinc-700 hover:border-amber-500/50 rounded-lg text-zinc-500 hover:text-amber-400 transition-colors bg-zinc-900/30 hover:bg-amber-500/5">
        <span className="text-lg leading-none">&#9651;</span>
        <span className="font-mono text-xs">Dachberechnung</span>
        {result?.roof_area_total_m2 && <span className="font-mono text-[10px] text-amber-400">{result.roof_area_total_m2} m²</span>}
      </button>
    );
  }

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden" data-testid="roof-geometry-card">
      <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-800/50">
        <div className="flex items-center gap-2">
          <span className="text-amber-400 text-sm">&#9651;</span>
          <span className="font-mono text-xs text-zinc-300">Dachberechnung</span>
        </div>
        <button onClick={() => setOpen(false)} className="p-1 hover:bg-zinc-800 rounded-md">
          <X className="w-3 h-3 text-zinc-500" />
        </button>
      </div>
      <div className="p-3">
        <form onSubmit={handleSave} className="space-y-2">
          <div className="space-y-1.5">
            <div>
              <label className="font-mono text-[9px] text-zinc-500 block mb-0.5">Traufhöhe (m)</label>
              <input type="text" inputMode="decimal" value={eave} onChange={e => setEave(e.target.value)} placeholder="z.B. 1.20"
                className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-xs font-mono text-white focus:border-amber-500 focus:outline-none" />
            </div>
            <div>
              <label className="font-mono text-[9px] text-zinc-500 block mb-0.5">Firsthöhe (m)</label>
              <input type="text" inputMode="decimal" value={ridge} onChange={e => setRidge(e.target.value)} placeholder="z.B. 4.50"
                className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-xs font-mono text-white focus:border-amber-500 focus:outline-none" />
            </div>
            <div>
              <label className="font-mono text-[9px] text-zinc-500 block mb-0.5">Dachneigung (°)</label>
              <input type="text" inputMode="decimal" value={angle} onChange={e => setAngle(e.target.value)} placeholder="z.B. 35"
                className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-xs font-mono text-white focus:border-amber-500 focus:outline-none" />
            </div>
          </div>
          <button type="submit" disabled={saving || (!eave && !ridge && !angle)}
            className="w-full px-2 py-1.5 bg-amber-600 hover:bg-amber-500 disabled:bg-zinc-700 disabled:text-zinc-500 text-white text-xs font-mono rounded transition-colors">
            {saving ? 'Berechne...' : 'Berechnen'}
          </button>
        </form>
        {result?.slope_length_m && (
          <div className="mt-3 pt-2 border-t border-zinc-700/30 space-y-0.5 font-mono text-[10px]">
            <div className="font-mono text-[9px] text-zinc-400 uppercase tracking-wider mb-1">Ergebnis</div>
            <div className="flex justify-between"><span className="text-zinc-500">Höhendiff.</span><span className="text-amber-400">{result.height_diff_m} m</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">Schräglänge</span><span className="text-amber-400">{result.slope_length_m} m</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">Horiz. Tiefe</span><span className="text-amber-400">{result.horizontal_depth_m} m</span></div>
            {result.roof_area_per_side_m2 && (<>
              <div className="flex justify-between"><span className="text-zinc-500">Dachfl./Seite</span><span className="text-green-400">{result.roof_area_per_side_m2} m²</span></div>
              <div className="flex justify-between"><span className="text-zinc-300 font-bold">Dachfl. gesamt</span><span className="text-green-400 font-bold">{result.roof_area_total_m2} m²</span></div>
            </>)}
          </div>
        )}
      </div>
    </div>
  );
}


function DGCard({ projectId, project, visibleLevels, setVisibleLevels, onUpload, isUploading, floors, onOpenAnalysis, deleteFloor, onUpdate, isAdmin }) {
  const [expanded, setExpanded] = useState(visibleLevels.has(4));
  const [roofOpen, setRoofOpen] = useState(false);
  const [roofType, setRoofType] = useState(project?.roof_geometry?.roof_type || '');
  const [eave, setEave] = useState('');
  const [ridge, setRidge] = useState('');
  const [angle, setAngle] = useState('');
  const [innerHeight, setInnerHeight] = useState('');
  const [flatArea, setFlatArea] = useState('');
  const [overhang, setOverhang] = useState('');
  const [saving, setSaving] = useState(false);
  const [roofResult, setRoofResult] = useState(null);
  const [gptChecking, setGptChecking] = useState(false);

  // Auto-fill from section analysis + saved data
  useEffect(() => {
    const rg = project?.roof_geometry;
    if (rg) {
      if (rg.roof_type) setRoofType(rg.roof_type);
      if (rg.eave_height_m) setEave(String(rg.eave_height_m));
      if (rg.ridge_height_m) setRidge(String(rg.ridge_height_m));
      if (rg.angle_deg) setAngle(String(rg.angle_deg));
      if (rg.inner_wall_height_m) setInnerHeight(String(rg.inner_wall_height_m));
      if (rg.overhang_m) setOverhang(String(rg.overhang_m));
      setRoofResult(rg);
    }
    // Pre-fill from section analysis
    const sa = project?.section_analysis;
    if (sa) {
      if (sa.roof_angle_deg && !angle) setAngle(String(sa.roof_angle_deg));
      if (!eave) {
        // Look for eave/Drempel height in section data
        const derived = sa.derived || {};
        if (derived.eave_height_m) setEave(String(derived.eave_height_m));
        if (derived.drempel_m) setEave(String(derived.drempel_m));
      }
      if (!ridge && sa.derived?.ridge_height_m) setRidge(String(sa.derived.ridge_height_m));
    }
  }, [project]);

  const handleSave = async (e) => {
    e.preventDefault();
    const body = {
      roof_type: roofType,
      overhang_m: overhang ? parseFloat(overhang.replace(',', '.')) : 0,
      inner_wall_height_m: innerHeight ? parseFloat(innerHeight.replace(',', '.')) : null,
    };
    if (roofType === 'flat') {
      body.flat_area_m2 = flatArea ? parseFloat(flatArea.replace(',', '.')) : null;
    } else {
      body.eave_height_m = parseFloat(eave.replace(',', '.'));
      body.ridge_height_m = parseFloat(ridge.replace(',', '.'));
      body.angle_deg = parseFloat(angle.replace(',', '.'));
    }
    setSaving(true);
    try {
      const resp = await fetch(`${API}/api/projects/${projectId}/roof-geometry`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (resp.ok) { const d = await resp.json(); setRoofResult(d.roof_geometry); if (onUpdate) onUpdate(); }
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const triggerGpt = async () => {
    setGptChecking(true);
    const gptBody = {
      roof_type: roofType, overhang_m: overhang ? parseFloat(overhang.replace(',','.')) : 0, use_gpt: true,
      inner_wall_height_m: innerHeight ? parseFloat(innerHeight.replace(',', '.')) : null,
    };
    if (roofType === 'flat') { gptBody.flat_area_m2 = flatArea ? parseFloat(flatArea.replace(',','.')) : null; }
    else { gptBody.eave_height_m = parseFloat(eave.replace(',','.')); gptBody.ridge_height_m = parseFloat(ridge.replace(',','.')); gptBody.angle_deg = parseFloat(angle.replace(',','.')); }
    try {
      const resp = await fetch(`${API}/api/projects/${projectId}/roof-geometry`, {
        method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify(gptBody),
      });
      if (resp.ok) { const d = await resp.json(); setRoofResult(d.roof_geometry); }
    } catch(e) { console.error(e); }
    finally { setGptChecking(false); }
  };

  const summary = roofResult?.roof_area_total_m2 ? `${roofResult.roof_area_total_m2} m²` : '';

  if (!expanded) {
    return (
      <button onClick={() => { setExpanded(true); setVisibleLevels(prev => new Set([...prev, 4])); }}
        data-testid="add-dg-btn"
        className="w-full flex items-center justify-center gap-2 py-2.5 border border-dashed border-amber-500/30 hover:border-amber-500/60 rounded-lg text-zinc-500 hover:text-amber-400 transition-colors bg-amber-500/5 hover:bg-amber-500/10">
        <span className="text-sm">&#9651;</span>
        <span className="font-mono text-xs">Dachgeschoss</span>
        {summary && <span className="font-mono text-[10px] text-amber-400 ml-1">({summary})</span>}
      </button>
    );
  }

  return (
    <div className="bg-zinc-900 border border-amber-500/20 rounded-lg overflow-hidden" data-testid="dg-card">
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800/50 bg-amber-500/5">
        <div className="flex items-center gap-3">
          <div className="w-8 h-7 rounded-md bg-amber-500/10 flex items-center justify-center text-[10px] text-amber-400 font-bold">DG</div>
          <span className="font-mono text-sm font-medium text-zinc-200">Dachgeschoss</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onUpload} disabled={isUploading}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-md font-mono text-xs text-zinc-400 hover:text-white transition-colors disabled:opacity-40">
            {isUploading ? <span className="text-blue-400">Analyse...</span> : <><Upload className="w-3 h-3" /> Grundriss</>}
          </button>
          <button onClick={() => { setExpanded(false); setVisibleLevels(prev => { const n = new Set(prev); n.delete(4); return n; }); }}
            className="p-1.5 hover:bg-red-500/20 rounded-md transition-colors">
            <X className="w-3.5 h-3.5 text-zinc-600 hover:text-red-400" />
          </button>
        </div>
      </div>

      {floors.map(floor => (
        <div key={floor.analysis_id}>
          <div className="group flex items-center gap-3 px-4 py-2 hover:bg-zinc-800/50 border-b border-zinc-800/20">
            <button onClick={() => onOpenAnalysis(floor.analysis_id)} className="flex-1 flex items-center gap-3 text-left min-w-0">
              <FileImage className="w-4 h-4 text-amber-400 flex-shrink-0" />
              <span className="font-mono text-xs text-zinc-300 truncate">{floor.filename}</span>
              <ChevronRight className="w-3 h-3 text-zinc-600 flex-shrink-0" />
            </button>
            <button onClick={() => deleteFloor(floor.analysis_id, floor.filename)}
              className="p-1 hover:bg-red-500/20 rounded-md opacity-0 group-hover:opacity-100">
              <Trash2 className="w-3 h-3 text-red-400" />
            </button>
          </div>
          <FloorElementList analysisId={floor.analysis_id} />
        </div>
      ))}

      {/* Dachberechnung */}
      {isAdmin ? (
      <div className="border-t border-zinc-800/30">
        <button onClick={() => setRoofOpen(!roofOpen)}
          className="w-full flex items-center justify-between px-4 py-2 hover:bg-zinc-800/30 transition-colors">
          <span className="flex items-center gap-2 font-mono text-[10px] text-amber-400">
            <span className="text-sm">&#9651;</span> Dachberechnung
          </span>
          {summary && !roofOpen && <span className="font-mono text-[10px] text-green-400">{summary}</span>}
          {roofOpen ? <ChevronDown className="w-3 h-3 text-zinc-500" /> : <ChevronRight className="w-3 h-3 text-zinc-500" />}
        </button>

        {roofOpen && (
          <div className="px-4 pb-3">
            {/* Dachtyp */}
            <div className="flex gap-1.5 mb-3">
              {[{ key: 'flat', label: 'Flachdach', icon: '▬' }, { key: 'gable', label: 'Satteldach', icon: '△' }, { key: 'hip', label: 'Walmdach', icon: '⬠' }].map(t => (
                <button key={t.key} onClick={() => { setRoofType(t.key); if (t.key === 'flat') setFlatArea(''); }} type="button"
                  className={`flex-1 flex flex-col items-center gap-0.5 py-2 rounded border transition-colors font-mono text-[10px] ${roofType === t.key ? 'bg-amber-500/15 border-amber-500/40 text-amber-400' : 'bg-zinc-800/50 border-zinc-700/50 text-zinc-500 hover:text-zinc-300'}`}>
                  <span className="text-base leading-none">{t.icon}</span><span>{t.label}</span>
                </button>
              ))}
            </div>

            {roofType && (
              <form onSubmit={handleSave} className="space-y-2">
                {roofType === 'flat' ? (
                  <div>
                    <label className="font-mono text-[9px] text-zinc-500 block mb-0.5">Dachfläche (m²) — leer = auto</label>
                    <input type="text" inputMode="decimal" value={flatArea} onChange={e => setFlatArea(e.target.value)}
                      placeholder={roofResult?.roof_area_total_m2 ? `${roofResult.roof_area_total_m2} m² (auto)` : ''}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-xs font-mono text-white focus:border-amber-500 focus:outline-none" />
                  </div>
                ) : (<>
                  {/* Schnitt-Daten Hinweis */}
                  {project?.section_analysis && (
                    <div className="text-[9px] font-mono text-zinc-600 bg-zinc-800/30 rounded px-2 py-1">
                      Werte aus Schnitt-Analyse vorausgefüllt — manuell änderbar
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <div><label className="font-mono text-[9px] text-zinc-500 block">Drempel / Traufhöhe (m)</label>
                      <input type="text" inputMode="decimal" value={eave} onChange={e => setEave(e.target.value)} placeholder="1.20"
                        className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-[10px] font-mono text-white focus:border-amber-500 focus:outline-none" /></div>
                    <div><label className="font-mono text-[9px] text-zinc-500 block">Firsthöhe (m)</label>
                      <input type="text" inputMode="decimal" value={ridge} onChange={e => setRidge(e.target.value)} placeholder="4.50"
                        className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-[10px] font-mono text-white focus:border-amber-500 focus:outline-none" /></div>
                    <div><label className="font-mono text-[9px] text-zinc-500 block">Dachneigung (°)</label>
                      <input type="text" inputMode="decimal" value={angle} onChange={e => setAngle(e.target.value)} placeholder="35"
                        className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-[10px] font-mono text-white focus:border-amber-500 focus:outline-none" /></div>
                    <div><label className="font-mono text-[9px] text-zinc-500 block">Innenwandhöhe DG (m)</label>
                      <input type="text" inputMode="decimal" value={innerHeight} onChange={e => setInnerHeight(e.target.value)} placeholder="2.35"
                        className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-[10px] font-mono text-white focus:border-amber-500 focus:outline-none" /></div>
                  </div>
                </>)}
                <div>
                  <label className="font-mono text-[9px] text-zinc-500 block mb-0.5">Dachüberstand (m)</label>
                  <input type="text" inputMode="decimal" value={overhang} onChange={e => setOverhang(e.target.value)} placeholder="0.30"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-xs font-mono text-white focus:border-amber-500 focus:outline-none" />
                </div>
                <div className="flex gap-1.5">
                  <button type="submit" disabled={saving}
                    className="flex-1 px-2 py-1.5 bg-amber-600 hover:bg-amber-500 disabled:bg-zinc-700 text-white text-[10px] font-mono rounded transition-colors">
                    {saving ? 'Berechne...' : 'Berechnen'}
                  </button>
                  <button type="button" disabled={gptChecking || !roofResult?.roof_area_total_m2} onClick={triggerGpt}
                    className="px-2 py-1.5 bg-purple-600/30 hover:bg-purple-500/40 disabled:bg-zinc-700 border border-purple-500/30 text-purple-300 disabled:text-zinc-500 text-[10px] font-mono rounded transition-colors"
                    title="KI-Plausibilitätsprüfung">
                    {gptChecking ? '...' : 'Prüfen'}
                  </button>
                </div>
              </form>
            )}

            {/* ── Ergebnisse ───────────────────────────── */}
            {roofResult && roofResult.roof_area_total_m2 && (
              <div className="mt-3 pt-2 border-t border-zinc-700/30 space-y-1">
                <div className="font-mono text-[9px] text-zinc-400 uppercase tracking-wider">Ergebnis</div>

                {/* Wandhöhen DG */}
                {roofType !== 'flat' && (
                  <div className="p-2 bg-zinc-800/30 rounded space-y-0.5 font-mono text-[10px]">
                    <div className="text-[9px] text-zinc-500 uppercase">Wandhöhen DG</div>
                    <div className="flex justify-between"><span className="text-zinc-400">Außenwand (Drempel)</span><span className="text-amber-400">{roofResult.eave_height_m} m</span></div>
                    {roofResult.inner_wall_height_m && (
                      <div className="flex justify-between"><span className="text-zinc-400">Innenwand</span><span className="text-amber-400">{roofResult.inner_wall_height_m} m</span></div>
                    )}
                    <div className="flex justify-between"><span className="text-zinc-400">First (max.)</span><span className="text-amber-400">{roofResult.ridge_height_m} m</span></div>
                  </div>
                )}

                {/* Dachmaße */}
                <div className="p-2 bg-zinc-800/30 rounded space-y-0.5 font-mono text-[10px]">
                  <div className="text-[9px] text-zinc-500 uppercase">Dachfläche</div>
                  {roofResult.overhang_m > 0 && <div className="flex justify-between"><span className="text-zinc-500">Dachüberstand</span><span className="text-zinc-400">{roofResult.overhang_m} m</span></div>}
                  {roofType !== 'flat' && (<>
                    <div className="flex justify-between"><span className="text-zinc-500">Gebäude</span><span className="text-zinc-400">{roofResult.building_length_m}×{roofResult.building_width_m} m</span></div>
                    <div className="flex justify-between"><span className="text-zinc-500">Schräglänge</span><span className="text-amber-400">{roofResult.slope_length_m} m</span></div>
                    {roofResult.ridge_length_m !== undefined && <div className="flex justify-between"><span className="text-zinc-500">Firstlänge</span><span className="text-zinc-400">{roofResult.ridge_length_m} m</span></div>}
                  </>)}
                  {roofResult.area_per_side_m2 && <div className="flex justify-between"><span className="text-zinc-500">Pro Seite</span><span className="text-green-400">{roofResult.area_per_side_m2} m²</span></div>}
                  {roofResult.area_main_sides_m2 && <div className="flex justify-between"><span className="text-zinc-500">Hauptflächen</span><span className="text-green-400">{roofResult.area_main_sides_m2} m²</span></div>}
                  {roofResult.area_hip_sides_m2 && <div className="flex justify-between"><span className="text-zinc-500">Walmflächen</span><span className="text-green-400">{roofResult.area_hip_sides_m2} m²</span></div>}
                  <div className="flex justify-between pt-1 border-t border-zinc-700/20">
                    <span className="text-white font-bold">Dachfläche gesamt</span>
                    <span className="text-green-400 font-bold text-xs">{roofResult.roof_area_total_m2} m²</span>
                  </div>
                </div>

                {roofResult.gpt_check && (
                  <div className={`p-1.5 rounded text-[9px] font-mono ${roofResult.gpt_check.plausible ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                    Prüfung: {roofResult.gpt_check.comment}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      ) : (
      <div className="border-t border-zinc-800/30 relative" data-testid="dach-coming-soon">
        <div className="blur-[3px] pointer-events-none select-none opacity-40 py-3 px-4">
          <div className="flex items-center gap-2 font-mono text-[10px] text-amber-400">
            <span className="text-sm">&#9651;</span> Dachberechnung
          </div>
        </div>
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <div className="px-4 py-2 bg-zinc-900/90 border border-amber-500/30 rounded-lg text-center">
            <span className="font-mono text-[10px] text-amber-400 font-bold tracking-wider uppercase">Coming Soon</span>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}


export function ProjectDetailView({ projectId, onBack, onOpenAnalysis, onUploadToFloor }) {
  const { user } = useAuth();
  const modal = useModal();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const [editingName, setEditingName] = useState(false);
  const [editName, setEditName] = useState('');
  const [showPaywall, setShowPaywall] = useState(false);
  const fileInputRef = useRef(null);
  const sectionInputRef = useRef(null);
  const [selectedFloor, setSelectedFloor] = useState(null);
  const [visibleLevels, setVisibleLevels] = useState(new Set([0]));
  const [sectionUploading, setSectionUploading] = useState(false);
  const [sectionAnalyzing, setSectionAnalyzing] = useState(false);
  const [sectionZoom, setSectionZoom] = useState(false);
  const [mergeLvEditorOpen, setMergeLvEditorOpen] = useState(false);
  const [mergeLvEditorIds, setMergeLvEditorIds] = useState([]);

  const fetchProject = useCallback(async () => {
    try {
      const resp = await fetch(`${API}/api/projects/${projectId}`);
      if (!resp.ok) throw new Error('Not found');
      const data = await resp.json();
      setProject(data);
      // Derive visible levels: always include 0 + any levels that have analyses
      const levels = new Set([0]);
      for (const floor of (data.floors || [])) {
        levels.add(floor.floor_level ?? 0);
      }
      setVisibleLevels(levels);
    } catch (e) {
      console.error('Failed to fetch project', e);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchProject(); }, [fetchProject]);

  const deleteFloor = async (analysisId, filename) => {
    if (!await modal.confirm(`Grundriss "${filename}" wirklich löschen?`, { title: 'Grundriss löschen', confirmText: 'Löschen', type: 'warning' })) return;
    try {
      await fetch(`${API}/api/projects/${projectId}/floors/${analysisId}`, { method: 'DELETE' });
      fetchProject();
    } catch (e) {
      console.error('Failed to delete floor', e);
    }
  };

  const handleFileSelect = (floorLevel) => {
    // Trial mode: only 1 grundriss per project allowed for non-privileged users
    const isPrivileged = user?.role === 'admin' || user?.subscription_status === 'active';
    const totalFloors = (project?.floors || []).length;
    if (!isPrivileged && totalFloors >= 1) {
      setShowPaywall(true);
      return;
    }
    setSelectedFloor(floorLevel);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || selectedFloor === null) return;
    e.target.value = '';
    setUploading(selectedFloor);
    try {
      await onUploadToFloor(file, projectId, selectedFloor);
      fetchProject();
    } catch (err) {
      console.error('Upload failed', err);
    } finally {
      setUploading(null);
      setSelectedFloor(null);
    }
  };

  const saveProjectName = async () => {
    if (!editName.trim()) return;
    try {
      await fetch(`${API}/api/projects/${projectId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editName.trim() }),
      });
      setEditingName(false);
      fetchProject();
    } catch (e) {
      console.error('Failed to update project name', e);
    }
  };

  const addLevelAbove = () => {
    const maxVisible = Math.max(...visibleLevels);
    const nextUp = maxVisible + 1;
    if (nextUp <= 4) {
      setVisibleLevels(prev => new Set([...prev, nextUp]));
    }
  };

  const addLevelBelow = () => {
    const minVisible = Math.min(...visibleLevels);
    const nextDown = minVisible - 1;
    if (nextDown >= -2) {
      setVisibleLevels(prev => new Set([...prev, nextDown]));
    }
  };

  const handleSectionUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setSectionUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const resp = await fetch(`${API}/api/projects/${projectId}/section`, {
        method: 'POST',
        body: formData,
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` },
      });
      if (resp.ok) fetchProject();
    } catch (err) {
      console.error('Section upload failed', err);
    } finally {
      setSectionUploading(false);
    }
  };

  const analyzeSection = async () => {
    setSectionAnalyzing(true);
    try {
      const resp = await fetch(`${API}/api/projects/${projectId}/section/analyze`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` },
      });
      if (resp.ok) {
        const data = await resp.json();
        if (data.status === 'processing') {
          // Poll until completed
          for (let i = 0; i < 60; i++) {
            await new Promise(r => setTimeout(r, 3000));
            try {
              const pollResp = await fetch(`${API}/api/projects/${projectId}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` },
              });
              if (pollResp.ok) {
                const proj = await pollResp.json();
                const status = proj.section_analysis_status;
                if (status === 'completed') {
                  fetchProject();
                  break;
                }
                if (status === 'failed') {
                          await modal.alert(proj.section_analysis_error || 'Schnitt-Analyse fehlgeschlagen', { title: 'Fehler', type: 'warning' });
                  break;
                }
              }
            } catch { /* retry */ }
          }
        } else if (data.status === 'ok') {
          fetchProject();
        } else {
          await modal.alert(data.message || 'Analyse fehlgeschlagen', { title: 'Fehler', type: 'warning' });
        }
      }
    } catch (err) {
      console.error('Section analysis failed', err);
    } finally {
      setSectionAnalyzing(false);
    }
  };

  const deleteSection = async () => {
    if (!await modal.confirm('Schnitt wirklich löschen?', { title: 'Schnitt löschen', confirmText: 'Löschen', type: 'warning' })) return;
    try {
      await fetch(`${API}/api/projects/${projectId}/section`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` },
      });
      fetchProject();
    } catch (err) {
      console.error('Section delete failed', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <span className="font-mono text-sm text-zinc-500">Projekt wird geladen...</span>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-zinc-950 flex flex-col items-center justify-center gap-4">
        <span className="font-mono text-sm text-zinc-500">Projekt nicht gefunden.</span>
        <button onClick={onBack} className="font-mono text-sm text-blue-400 hover:text-blue-300">Zurück</button>
      </div>
    );
  }

  // Group floors by floor_level
  const floorsByLevel = {};
  for (const floor of (project.floors || [])) {
    const lvl = String(floor.floor_level ?? 0);
    if (!floorsByLevel[lvl]) floorsByLevel[lvl] = [];
    floorsByLevel[lvl].push(floor);
  }

  // Sort visible levels top-to-bottom (highest first)
  const sortedVisibleLevels = [...visibleLevels].sort((a, b) => b - a);
  const maxVisible = Math.max(...visibleLevels);
  const minVisible = Math.min(...visibleLevels);
  const canAddAbove = maxVisible < 4;
  const canAddBelow = minVisible > -2;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      {/* Schnitt-Zoom-Modal */}
      {sectionZoom && project?.section_image && (
        <div className="fixed inset-0 z-[200] bg-black/90 backdrop-blur-sm flex items-center justify-center p-8"
          onClick={() => setSectionZoom(false)} data-testid="section-zoom-modal">
          <button onClick={() => setSectionZoom(false)}
            className="absolute top-4 right-4 p-2 bg-zinc-800 hover:bg-zinc-700 rounded-full text-white z-10">
            <X className="w-5 h-5" />
          </button>
          <img
            src={`data:image/png;base64,${project.section_image}`}
            alt="Gebäudeschnitt (Vollbild)"
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {showPaywall && (
        <div className="fixed inset-0 z-[100] bg-zinc-950/95 backdrop-blur-sm overflow-y-auto" data-testid="paywall-overlay-detail">
          <Paywall
            title="Premium-Funktion"
            subtitle="Im kostenlosen Test können Sie 1 Grundriss pro Projekt analysieren. Schalten Sie LAIzyBlueprint frei für unbegrenzte Pläne."
            hideTrialOption
            onClose={() => setShowPaywall(false)}
          />
        </div>
      )}

      <input ref={fileInputRef} type="file" className="hidden" accept="image/*,.pdf,.docx,.doc,.rtf,.odt"
        onChange={handleFileChange} data-testid="floor-file-input" />

      {/* Header */}
      <div className="border-b border-zinc-800 bg-zinc-900/80 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-4">
          <button data-testid="back-to-projects-btn" onClick={onBack}
            className="p-2 hover:bg-zinc-800 rounded-md transition-colors">
            <ArrowLeft className="w-4 h-4 text-zinc-400" />
          </button>
          <div className="flex-1 min-w-0">
            {editingName ? (
              <div className="flex items-center gap-2">
                <input
                  data-testid="edit-project-name-input"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveProjectName(); if (e.key === 'Escape') setEditingName(false); }}
                  className="px-3 py-1.5 bg-zinc-800 border border-zinc-600 rounded-md font-mono text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  autoFocus
                />
                <button onClick={saveProjectName} className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 rounded-md font-mono text-xs">Speichern</button>
                <button onClick={() => setEditingName(false)} className="p-1.5 hover:bg-zinc-800 rounded-md">
                  <X className="w-3.5 h-3.5 text-zinc-500" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <h1 className="font-mono text-lg font-medium truncate">{project.name}</h1>
                <button data-testid="edit-project-name-btn"
                  onClick={() => { setEditName(project.name); setEditingName(true); }}
                  className="p-1 hover:bg-zinc-800 rounded-md transition-colors opacity-60 hover:opacity-100">
                  <Pencil className="w-3 h-3 text-zinc-500" />
                </button>
              </div>
            )}
            {project.description && !editingName && (
              <p className="font-mono text-xs text-zinc-500 mt-0.5">{project.description}</p>
            )}
          </div>
          <div className="font-mono text-xs text-zinc-500">
            {project.floors?.length || 0} Grundrisse
          </div>
        </div>
      </div>

      {/* Floor levels + Section panel */}
      <div className="max-w-6xl mx-auto px-6 py-6">
        <div className="flex gap-6">
          {/* LEFT: Floor levels */}
          <div className="flex-1 space-y-3">
          {/* Dachgeschoss + Berechnung (immer oben) */}
          <DGCard projectId={projectId} project={project}
            visibleLevels={visibleLevels} setVisibleLevels={setVisibleLevels}
            onUpload={() => handleFileSelect(4)}
            isUploading={uploading !== null && String(uploading) === '4'}
            floors={floorsByLevel['4'] || []}
            onOpenAnalysis={onOpenAnalysis}
            deleteFloor={deleteFloor}
            onUpdate={fetchProject}
            isAdmin={user?.role === 'admin'} />

          {/* + Button oben: Geschoss darüber hinzufügen (nur OG, nicht DG) */}
          {canAddAbove && maxVisible < 3 && (
            <button
              data-testid="add-level-above-btn"
              onClick={addLevelAbove}
              className="w-full flex items-center justify-center gap-2 py-2.5 border border-dashed border-zinc-700 hover:border-zinc-500 rounded-lg text-zinc-500 hover:text-zinc-300 transition-colors group"
            >
              <Plus className="w-3.5 h-3.5" />
              <span className="font-mono text-xs">{FLOOR_LEVELS[String(maxVisible + 1)]} hinzufügen</span>
            </button>
          )}

          {/* Sichtbare Geschosse (höchstes zuerst), OHNE DG (level 4) */}
          {sortedVisibleLevels.filter(n => n !== 4).map((numLvl) => {
            const lvl = String(numLvl);
            const levelName = FLOOR_LEVELS[lvl];
            const floors = floorsByLevel[lvl] || [];
            const isUploading = uploading !== null && String(uploading) === lvl;

            return (
              <div key={lvl} className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden" data-testid={`floor-level-${lvl}`}>
                {/* Level header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800/50">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-7 rounded-md bg-zinc-800 flex items-center justify-center text-[10px] text-zinc-400 font-bold tracking-tight">
                      {FLOOR_ABBR[lvl] || lvl}
                    </div>
                    <span className="font-mono text-sm text-zinc-300">{levelName}</span>
                    {floors.length > 0 && (
                      <span className="font-mono text-[10px] text-zinc-600 bg-zinc-800 px-2 py-0.5 rounded-full">
                        {floors.length}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      data-testid={`upload-to-level-${lvl}`}
                      onClick={() => handleFileSelect(parseInt(lvl))}
                      disabled={isUploading}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-md font-mono text-xs text-zinc-400 hover:text-white transition-colors disabled:opacity-40"
                    >
                      {isUploading ? (
                        <span className="text-blue-400">Wird analysiert...</span>
                      ) : (
                        <><Upload className="w-3 h-3" /> Hochladen</>
                      )}
                    </button>
                    <button
                      data-testid={`remove-level-${lvl}`}
                      onClick={async () => {
                        if (floors.length > 0) {
                          if (!await modal.confirm(`${levelName} hat ${floors.length} Grundriss(e). Ebene und alle Grundrisse wirklich entfernen?`, { title: 'Ebene löschen', confirmText: 'Löschen', type: 'warning' })) return;
                          for (const f of floors) {
                            await fetch(`${API}/api/projects/${projectId}/floors/${f.analysis_id}`, { method: 'DELETE' });
                          }
                        }
                        setVisibleLevels(prev => { const next = new Set(prev); next.delete(numLvl); return next; });
                        if (floors.length > 0) fetchProject();
                      }}
                      className="p-1.5 hover:bg-red-500/20 border border-transparent hover:border-red-500/30 rounded-md transition-colors"
                      title={`${levelName} entfernen`}
                    >
                      <X className="w-3.5 h-3.5 text-zinc-600 hover:text-red-400" />
                    </button>
                  </div>
                </div>

                {/* Floor entries */}
                {floors.length > 0 && (
                  <div className="divide-y divide-zinc-800/50">
                    {floors.map((floor) => (
                      <div key={floor.analysis_id}>
                        <div
                          className="group flex items-center gap-3 px-4 py-3 hover:bg-zinc-800/50 transition-colors"
                          data-testid={`floor-entry-${floor.analysis_id}`}>
                          <button
                            data-testid={`open-analysis-${floor.analysis_id}`}
                            onClick={() => onOpenAnalysis(floor.analysis_id)}
                            className="flex-1 flex items-center gap-3 text-left min-w-0">
                            <FileImage className="w-4 h-4 text-blue-400 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <span className="font-mono text-xs text-zinc-300 block truncate">{floor.filename}</span>
                              <div className="flex items-center gap-3 mt-0.5">
                                <span className="font-mono text-[10px] text-zinc-600">
                                  {new Date(floor.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                </span>
                                {floor.processing_time && (
                                  <span className="font-mono text-[10px] text-zinc-600">{floor.processing_time}s</span>
                                )}
                                {floor.svg_data?.metadata?.total_area_m2 && (
                                  <span className="font-mono text-[10px] text-green-500/70">{floor.svg_data.metadata.total_area_m2} m²</span>
                                )}
                              </div>
                            </div>
                            <ChevronRight className="w-3.5 h-3.5 text-zinc-600 group-hover:text-zinc-400 flex-shrink-0" />
                          </button>
                          <button
                            data-testid={`delete-floor-${floor.analysis_id}`}
                            onClick={() => deleteFloor(floor.analysis_id, floor.filename)}
                            className="p-1.5 hover:bg-red-500/20 rounded-md transition-all opacity-0 group-hover:opacity-100 flex-shrink-0"
                          >
                            <Trash2 className="w-3.5 h-3.5 text-red-400" />
                          </button>
                        </div>
                        <FloorElementList analysisId={floor.analysis_id} />
                      </div>
                    ))}
                  </div>
                )}

                {/* Empty state for this level */}
                {floors.length === 0 && (
                  <div className="px-4 py-3">
                    <span className="font-mono text-[10px] text-zinc-600">Kein Grundriss hochgeladen</span>
                  </div>
                )}
              </div>
            );
          })}

          {/* + Button unten: Geschoss darunter hinzufügen */}
          {canAddBelow && (
            <button
              data-testid="add-level-below-btn"
              onClick={addLevelBelow}
              className="w-full flex items-center justify-center gap-2 py-2.5 border border-dashed border-zinc-700 hover:border-zinc-500 rounded-lg text-zinc-500 hover:text-zinc-300 transition-colors group"
            >
              <Plus className="w-3.5 h-3.5" />
              <span className="font-mono text-xs">{FLOOR_LEVELS[String(minVisible - 1)]} hinzufügen</span>
            </button>
          )}

          {/* Gesamt-LV: Alle Geschosse zusammenziehen */}
          {(() => {
            const allAnalysisIds = Object.values(floorsByLevel).flat().map(f => f.analysis_id);
            if (allAnalysisIds.length < 2) return null;
            return (
              <div className="p-4 bg-blue-500/5 border border-blue-500/20 rounded-lg space-y-3" data-testid="merge-lv-section">
                <div>
                  <span className="font-mono text-xs text-blue-300 font-medium">Gesamt-LV exportieren</span>
                  <p className="font-mono text-[10px] text-zinc-500 mt-0.5">
                    Alle {allAnalysisIds.length} Geschosse in einem LV zusammenführen
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    data-testid="merge-lv-btn"
                    onClick={() => {
                      const ids = allAnalysisIds.join(',');
                      window.open(`${API}/api/analyses/${ids}/export-pdf?merge=true`, '_blank');
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-white font-mono text-xs rounded-lg transition-colors"
                  >
                    <FileImage className="w-3.5 h-3.5" /> Elementliste PDF
                  </button>
                  <button
                    data-testid="merge-langtext-btn"
                    onClick={() => {
                      const ids = allAnalysisIds.join(',');
                      window.open(`${API}/api/analyses/${ids}/export-langtext-lv?merge=true`, '_blank');
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white font-mono text-xs rounded-lg transition-colors"
                  >
                    <FileImage className="w-3.5 h-3.5" /> LV-PDF
                  </button>
                  <button
                    data-testid="merge-lv-editor-btn"
                    onClick={() => {
                      setMergeLvEditorIds(allAnalysisIds);
                      setMergeLvEditorOpen(true);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-mono text-xs rounded-lg transition-colors"
                  >
                    <FileImage className="w-3.5 h-3.5" /> LV-Editor (alle Geschosse)
                  </button>
                </div>
              </div>
            );
          })()}

          </div>

          {/* RIGHT COLUMN: Schnitt + Dachgeometrie */}
          <div className="w-72 flex-shrink-0 space-y-3" data-testid="right-column">

            {/* Schnitt */}
            <div data-testid="section-panel">
            <input ref={sectionInputRef} type="file" className="hidden" accept="image/*,.pdf"
              onChange={handleSectionUpload} data-testid="section-file-input" />

            {project.section_image ? (
              <div className="h-full bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden flex flex-col">
                <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-800/50">
                  <div className="flex items-center gap-2">
                    <Scissors className="w-3.5 h-3.5 text-amber-400" />
                    <span className="font-mono text-xs text-zinc-300">Schnitt</span>
                  </div>
                  <button onClick={deleteSection} className="p-1 hover:bg-red-500/20 rounded-md transition-colors"
                    title="Schnitt löschen">
                    <Trash2 className="w-3 h-3 text-red-400" />
                  </button>
                </div>
                <div className="flex-1 p-2 flex flex-col gap-2">
                  <div className="relative group/img cursor-zoom-in" onClick={() => setSectionZoom(true)}>
                    <img
                      src={`data:image/png;base64,${project.section_image}`}
                      alt="Gebäudeschnitt"
                      className="w-full rounded border border-zinc-700 object-contain max-h-[400px]"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover/img:opacity-100 rounded transition-opacity">
                      <Eye className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <span className="font-mono text-[10px] text-zinc-500 truncate">{project.section_filename || 'Schnitt'}</span>
                  <button
                    data-testid="analyze-section-btn"
                    onClick={analyzeSection}
                    disabled={sectionAnalyzing}
                    className="flex items-center justify-center gap-1.5 px-3 py-2 bg-amber-600 hover:bg-amber-500 disabled:bg-zinc-700 disabled:text-zinc-500 text-white text-xs font-mono rounded-md transition-colors"
                  >
                    {sectionAnalyzing ? (
                      <><Loader2 className="w-3 h-3 animate-spin" /> Analysiere...</>
                    ) : (
                      <><Eye className="w-3 h-3" /> Schnitt analysieren</>
                    )}
                  </button>
                  {project.section_analysis && (
                    <div className="mt-1 p-2 bg-zinc-800 rounded border border-zinc-700">
                      <span className="font-mono text-[10px] text-zinc-400 uppercase tracking-wider">Ergebnisse</span>
                      {project.section_analysis.reference_elevation && (
                        <div className="mt-1 font-mono text-[10px] text-zinc-500">
                          {typeof project.section_analysis.reference_elevation === 'string'
                            ? project.section_analysis.reference_elevation
                            : (project.section_analysis.reference_elevation.datum
                                || project.section_analysis.reference_elevation.source_text
                                || S(project.section_analysis.reference_elevation, ''))}
                        </div>
                      )}
                      {project.section_analysis.floors?.map((f, i) => (
                        <div key={i} className="mt-1.5 p-1.5 bg-zinc-900/50 rounded">
                          <div className="flex justify-between font-mono text-[11px]">
                            <span className="text-zinc-300 font-bold">{S(f.name) || FLOOR_LEVELS[String(f.level)] || `Ebene ${f.level}`}</span>
                            <span className="text-amber-400 font-bold">{f.room_height_m || f.height_m ? `${S(f.room_height_m || f.height_m)} m` : '—'}</span>
                          </div>
                          {(f.floor_to_floor_m || f.ceiling_thickness_m || f.brh_m || f.usage) && (
                            <div className="mt-0.5 space-y-0.5">
                              {f.floor_to_floor_m && (
                                <div className="flex justify-between font-mono text-[10px]">
                                  <span className="text-zinc-500">Geschosshöhe</span>
                                  <span className="text-zinc-400">{S(f.floor_to_floor_m)} m</span>
                                </div>
                              )}
                              {f.ceiling_thickness_m && (
                                <div className="flex justify-between font-mono text-[10px]">
                                  <span className="text-zinc-500">Decke</span>
                                  <span className="text-zinc-400">{(parseFloat(S(f.ceiling_thickness_m, 0)) * 100).toFixed(0)} cm</span>
                                </div>
                              )}
                              {f.brh_m && (
                                <div className="flex justify-between font-mono text-[10px]">
                                  <span className="text-zinc-500">BRH</span>
                                  <span className="text-zinc-400">{S(f.brh_m)} m</span>
                                </div>
                              )}
                              {f.usage && (
                                <div className="font-mono text-[9px] text-zinc-600 mt-0.5">{S(f.usage)}</div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                      {project.section_analysis.derived && (
                        <div className="mt-2 pt-1.5 border-t border-zinc-700 space-y-0.5">
                          {project.section_analysis.derived.room_height_m && (
                            <div className="flex justify-between font-mono text-[11px]">
                              <span className="text-zinc-300">Raumhöhe</span>
                              <span className="text-amber-400 font-bold">{S(project.section_analysis.derived.room_height_m)} m</span>
                            </div>
                          )}
                          {project.section_analysis.derived.floor_to_floor_m && (
                            <div className="flex justify-between font-mono text-[10px]">
                              <span className="text-zinc-500">Geschosshöhe</span>
                              <span className="text-zinc-400">{S(project.section_analysis.derived.floor_to_floor_m)} m</span>
                            </div>
                          )}
                          {project.section_analysis.derived.ceiling_or_structure_thickness_m && (
                            <div className="flex justify-between font-mono text-[10px]">
                              <span className="text-zinc-500">Deckenaufbau</span>
                              <span className="text-zinc-400">{(parseFloat(S(project.section_analysis.derived.ceiling_or_structure_thickness_m, 0)) * 100).toFixed(0)} cm</span>
                            </div>
                          )}
                        </div>
                      )}
                      {(project.section_analysis.total_height_m || project.section_analysis.roof_angle_deg || project.section_analysis.derived?.total_height_m) && (
                        <div className="mt-2 pt-1.5 border-t border-zinc-700 space-y-0.5">
                          {project.section_analysis.total_height_m && (
                            <div className="flex justify-between font-mono text-[11px]">
                              <span className="text-zinc-300">Gesamthöhe</span>
                              <span className="text-white font-bold">{S(project.section_analysis.total_height_m)} m</span>
                            </div>
                          )}
                          {project.section_analysis.roof_angle_deg && (
                            <div className="flex justify-between font-mono text-[10px]">
                              <span className="text-zinc-500">Dachneigung</span>
                              <span className="text-zinc-400">{S(project.section_analysis.roof_angle_deg)}°</span>
                            </div>
                          )}
                        </div>
                      )}
                      {project.section_analysis.notes && (
                        <div className="mt-1.5 font-mono text-[9px] text-zinc-500 leading-tight">
                          {Array.isArray(project.section_analysis.notes)
                            ? project.section_analysis.notes.map(n => S(n)).filter(Boolean).join(' ')
                            : S(project.section_analysis.notes)}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <button
                onClick={() => sectionInputRef.current?.click()}
                disabled={sectionUploading}
                className="h-full min-h-[200px] w-full flex flex-col items-center justify-center gap-3 border border-dashed border-zinc-700 hover:border-amber-500/50 rounded-lg text-zinc-500 hover:text-amber-400 transition-colors bg-zinc-900/30 hover:bg-amber-500/5"
                data-testid="section-upload-btn"
              >
                {sectionUploading ? (
                  <Loader2 className="w-5 h-5 animate-spin text-amber-400" />
                ) : (
                  <Scissors className="w-5 h-5" />
                )}
                <span className="font-mono text-xs">{sectionUploading ? 'Wird hochgeladen...' : 'Schnitt hochladen'}</span>
              </button>
            )}
          </div>

          </div>
        </div>
      </div>
      <LVEditorModal
        open={mergeLvEditorOpen}
        onClose={() => setMergeLvEditorOpen(false)}
        analysisId={mergeLvEditorIds.join(',')}
        elementIds={[]}
      />
    </div>
  );
}

export default ProjectListView;
